import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { hasAnyCapability } from '../../utils/capabilities';
import Layout from '../layout/Layout';
import { Loader2 } from 'lucide-react';
import NotFoundPage from '../../pages/NotFoundPage';

/**
 * AdminOnlyRoute — Silent-404 guard for admin-level pages.
 *
 * Security model: any unauthorized visitor (logged-out, wrong role, or
 * sub-admin missing the required capability) sees a plain 404, identical to
 * a URL that doesn't exist. This prevents URL enumeration of the admin
 * surface by attackers (GitHub / GitLab / Notion pattern).
 *
 * Props:
 *   - allowRoles: roles permitted (default `['admin']`)
 *   - anyCap:     optional capability list; sub-admins must hold at least one.
 *                 Full admins bypass this.
 *
 * Race-condition safety: while `authLoading` is true (initial token verify),
 * a spinner is rendered so a legitimate admin's page does NOT flash 404 on
 * hard refresh before the /auth/me call resolves.
 */
const AdminOnlyRoute = ({ children, allowRoles = ['admin'], anyCap = null }) => {
  const { user, authLoading } = useAuth();

  // Race guard: wait for the token-verify round-trip before deciding.
  if (authLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#0A7EA4]" data-testid="admin-route-loading" />
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  // Logged out → 404 (do not reveal that the URL is protected).
  if (!user) {
    return <NotFoundPage />;
  }

  // Wrong role → 404.
  if (!allowRoles.includes(user.role)) {
    return <NotFoundPage />;
  }

  // Sub-admin lacking any of the required caps → 404. Full admins always pass.
  if (user.role !== 'admin' && Array.isArray(anyCap) && anyCap.length > 0) {
    if (!hasAnyCapability(user, anyCap)) {
      return <NotFoundPage />;
    }
  }

  return children;
};

export default AdminOnlyRoute;
